/**
 * LessonStatsService - Theo dõi thống kê học tập trong bài học
 */

export interface LessonStats {
  correctAnswers: number
  wrongAnswers: number
  hintsUsed: number
  aiQuestionsAsked: number
  totalQuestions: number
  startTime: number
  endTime?: number
}

class LessonStatsServiceClass {
  private stats: Map<string, LessonStats> = new Map()
  private currentLessonId: string | null = null

  /**
   * Bắt đầu theo dõi stats cho một bài học
   */
  startLesson(lessonId: string): void {
    this.currentLessonId = lessonId
    
    if (!this.stats.has(lessonId)) {
      this.stats.set(lessonId, {
        correctAnswers: 0,
        wrongAnswers: 0,
        hintsUsed: 0,
        aiQuestionsAsked: 0,
        totalQuestions: 0,
        startTime: Date.now()
      })
    }
    
    console.log(`📊 LessonStatsService: Started tracking for lesson ${lessonId}`)
  }

  /**
   * Ghi nhận câu trả lời đúng
   */
  recordCorrectAnswer(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats) {
      stats.correctAnswers++
      console.log(`✅ Correct answer recorded. Total: ${stats.correctAnswers}`)
    }
  }

  /**
   * Ghi nhận câu trả lời sai
   */
  recordWrongAnswer(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats) {
      stats.wrongAnswers++
      console.log(`❌ Wrong answer recorded. Total: ${stats.wrongAnswers}`)
    }
  }

  /**
   * Ghi nhận việc sử dụng gợi ý
   */
  recordHintUsed(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats) {
      stats.hintsUsed++
      console.log(`💡 Hint used. Total: ${stats.hintsUsed}`)
    }
  }

  /**
   * Ghi nhận câu hỏi AI
   */
  recordAIQuestion(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats) {
      stats.aiQuestionsAsked++
      console.log(`🤖 AI question asked. Total: ${stats.aiQuestionsAsked}`)
    }
  }

  /**
   * Đặt tổng số câu hỏi trong bài học
   */
  setTotalQuestions(total: number, lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    console.log(`🔍 DEBUG: setTotalQuestions called with total: ${total}, lessonId: ${lessonId}, resolved id: ${id}`)
    
    if (!id) {
      console.error(`❌ ERROR: No lessonId provided to setTotalQuestions`)
      return
    }

    const stats = this.stats.get(id)
    if (stats) {
      const oldTotal = stats.totalQuestions
      stats.totalQuestions = total
      console.log(`📝 Total questions set to: ${total} for lesson ${id} (was: ${oldTotal})`)
      
      // ✅ DEBUG: Kiểm tra logic đếm
      if (total <= 0) {
        console.warn(`⚠️ WARNING: Total questions is ${total} - this might be wrong!`)
      }
      if (total > 50) {
        console.warn(`⚠️ WARNING: Total questions is ${total} - seems too high!`)
      }
    } else {
      console.error(`❌ ERROR: No stats found for lessonId: ${id} when setting totalQuestions`)
      console.log(`🔍 Available lessonIds:`, Array.from(this.stats.keys()))
    }
  }

  /**
   * Kết thúc bài học
   */
  endLesson(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    const stats = this.stats.get(id)
    if (stats && !stats.endTime) {
      stats.endTime = Date.now()
      console.log(`🏁 Lesson ended. Duration: ${this.getDuration(id)} seconds`)
    }
  }

  /**
   * Lấy stats của bài học hiện tại
   */
  getCurrentStats(): LessonStats | null {
    if (!this.currentLessonId) return null
    return this.stats.get(this.currentLessonId) || null
  }

  /**
   * Lấy stats của một bài học cụ thể
   */
  getStats(lessonId: string): LessonStats | null {
    return this.stats.get(lessonId) || null
  }

  /**
   * Tính thời gian học (giây)
   */
  getDuration(lessonId?: string): number {
    const id = lessonId || this.currentLessonId
    if (!id) return 0

    const stats = this.stats.get(id)
    if (!stats) return 0

    const endTime = stats.endTime || Date.now()
    return Math.floor((endTime - stats.startTime) / 1000)
  }

  /**
   * Tính tỷ lệ chính xác (%)
   */
  getAccuracy(lessonId?: string): number {
    const id = lessonId || this.currentLessonId
    if (!id) return 0

    const stats = this.stats.get(id)
    if (!stats) return 0

    const totalAttempts = stats.correctAnswers + stats.wrongAnswers
    if (totalAttempts === 0) return 0

    return Math.round((stats.correctAnswers / totalAttempts) * 100)
  }

  /**
   * Tính tiến độ (%)
   */
  getProgress(lessonId?: string): number {
    const id = lessonId || this.currentLessonId
    if (!id) return 0

    const stats = this.stats.get(id)
    if (!stats || stats.totalQuestions === 0) return 0

    // ✅ FIX: Đếm tổng câu đã trả lời (đúng + sai), không chỉ câu đúng
    const totalAnswered = stats.correctAnswers + stats.wrongAnswers
    const progress = Math.round((totalAnswered / stats.totalQuestions) * 100)
    
    // ✅ DEBUG: Log progress calculation
    console.log(`📊 Progress calculation: ${totalAnswered}/${stats.totalQuestions} = ${progress}%`)
    
    return progress
  }

  /**
   * Reset stats của bài học
   */
  resetStats(lessonId?: string): void {
    const id = lessonId || this.currentLessonId
    if (!id) return

    this.stats.delete(id)
    console.log(`🔄 Stats reset for lesson ${id}`)
  }

  /**
   * Xóa bài học hiện tại
   */
  clearCurrentLesson(): void {
    this.currentLessonId = null
  }

  /**
   * Kiểm tra xem có nên hiển thị stats không
   * (Dùng khi user chuyển sang phần khác)
   */
  shouldShowStats(lessonId?: string): boolean {
    const id = lessonId || this.currentLessonId
    if (!id) return false

    const stats = this.stats.get(id)
    if (!stats) return false

    // Hiển thị stats nếu user đã tương tác (trả lời ít nhất 1 câu)
    return stats.correctAnswers > 0 || stats.wrongAnswers > 0
  }

  /**
   * Lấy summary stats để hiển thị quick preview
   */
  getSummary(lessonId?: string): string {
    const id = lessonId || this.currentLessonId
    if (!id) return ''

    const stats = this.stats.get(id)
    if (!stats) return ''

    const accuracy = this.getAccuracy(id)
    const progress = this.getProgress(id)
    
    return `${stats.correctAnswers}/${stats.totalQuestions} câu đúng | ${accuracy}% | ${progress}% hoàn thành`
  }
}

// Export singleton instance
export const LessonStatsService = new LessonStatsServiceClass()

